import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { MessageCircleHeart, ExternalLink, Heart } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";
import donationQr from "@/assets/donation-qr.png";

const FEEDBACK_URL =
  process.env.REACT_APP_FEEDBACK_URL || "https://forms.gle/eBwtzetszoF2QDH96";

function QrFallback() {
  // Decorative QR-like placeholder so the layout looks right even before the
  // real /donation-qr.png is added to /app/frontend/public/.
  const cells = [];
  for (let i = 0; i < 13; i++) {
    for (let j = 0; j < 13; j++) {
      const isCorner =
        (i < 3 && j < 3) || (i < 3 && j > 9) || (i > 9 && j < 3);
      const fill =
        isCorner || (i + j * 7 + i * 3) % 3 === 0 || (i * 5 + j) % 5 === 0;
      cells.push(
        <rect
          key={`${i}-${j}`}
          x={i * 14 + 8}
          y={j * 14 + 8}
          width={12}
          height={12}
          fill={fill ? "#0c0a09" : "transparent"}
        />
      );
    }
  }
  return (
    <svg
      viewBox="0 0 200 200"
      className="w-full h-full"
      aria-label="Donation QR placeholder"
    >
      <rect width={200} height={200} fill="#ffffff" />
      {cells}
    </svg>
  );
}

function DonationImage() {
  const [errored, setErrored] = useState(false);
  if (errored) return <QrFallback />;
  return (
    <img
      src={donationQr}
      alt="Donation QR"
      onError={() => setErrored(true)}
      className="w-full h-full object-contain bg-white"
      style={{ backgroundColor: "white" }}
    />
  );
}

export default function TopRightWidget({ open: openProp, onOpenChange, hideTrigger = false, triggerClassName }) {
  const t = useT();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = isControlled ? (onOpenChange || (() => {})) : setInternalOpen;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="topright-trigger"
            className={triggerClassName || "fixed top-4 right-4 md:top-5 md:right-5 z-40 hidden md:inline-flex items-center gap-2 bg-sky-700 text-white hover:bg-sky-800 transition-colors duration-100 rounded-sm px-3 py-2.5 min-h-[44px] shadow-md text-sm font-medium"}
            aria-label={t("widget.label")}
          >
            <MessageCircleHeart size={18} strokeWidth={1.75} />
            <span className="hidden sm:inline">{t("widget.label")}</span>
          </button>
        </DialogTrigger>
      )}

      <DialogContent
        data-testid="topright-dialog"
        className="max-w-2xl border border-stone-700 bg-stone-950 text-stone-100 rounded-sm p-0 overflow-hidden"
      >
        <div className="px-6 pt-6 pb-3 border-b border-stone-800">
          <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-400">
            HMMM
          </div>
          <h2 className="font-display text-xl tracking-tight mt-1">
            {t("widget.title")}
          </h2>
          <p className="text-xs text-stone-400 mt-1">{t("widget.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-stone-800">
          {/* LEFT — feedback */}
          <a
            href={FEEDBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="feedback-link"
            className="group flex flex-col items-center justify-center text-center gap-4 p-8 hover:bg-stone-900 transition-colors duration-100 min-h-[220px]"
          >
            <div className="h-14 w-14 rounded-sm bg-sky-700 text-white flex items-center justify-center group-hover:bg-sky-600 transition-colors duration-100">
              <MessageCircleHeart size={26} strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-base">{t("widget.feedback.title")}</div>
              <p className="text-xs text-stone-400 mt-1 max-w-[18rem]">
                {t("widget.feedback.body")}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-mono text-sky-400 group-hover:text-sky-300">
              {t("widget.feedback.cta")}
              <ExternalLink size={12} strokeWidth={2} />
            </span>
          </a>

          {/* RIGHT — donation */}
          <div
            className="flex flex-col items-center justify-center text-center gap-4 p-8 min-h-[220px]"
            data-testid="donation-block"
          >
            <div className="h-14 w-14 rounded-sm bg-amber-700/20 text-amber-400 flex items-center justify-center border border-amber-700/30">
              <Heart size={26} strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-base">{t("widget.donate.title")}</div>
              <p className="text-xs text-stone-400 mt-1 max-w-[18rem]">
                {t("widget.donate.body")}
              </p>
            </div>
            <div
              className="w-44 h-44 rounded-sm border border-stone-700 bg-white p-2 flex items-center justify-center"
              data-testid="donation-qr"
            >
              <DonationImage />
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-stone-800 text-[10px] tracking-[0.14em] uppercase font-mono text-stone-500">
          v2.5 · {t("widget.thanks")}
        </div>
      </DialogContent>
    </Dialog>
  );
}
